import { useCallback, type Dispatch, type SetStateAction } from 'react'
import type { NavigateFunction } from 'react-router-dom'
import type { SurveyDraftAnswer, SurveyQuestion } from './survey-question.types'
import { previousQuestionId, trimVisitedForBack } from './survey-navigation'

interface SurveyDraftNavigationParams {
  currentQuestion: SurveyQuestion | null
  visitedQuestionIds: string[]
  instrumentId: string | undefined
  navigate: NavigateFunction
  setAnswerMap: Dispatch<SetStateAction<Map<string, SurveyDraftAnswer>>>
  setVisitedQuestionIds: Dispatch<SetStateAction<string[]>>
  setLocalQuestionId: Dispatch<SetStateAction<string | null>>
  setShowRequiredError: Dispatch<SetStateAction<boolean>>
}

export function useSurveyDraftNavigation({
  currentQuestion,
  visitedQuestionIds,
  instrumentId,
  navigate,
  setAnswerMap,
  setVisitedQuestionIds,
  setLocalQuestionId,
  setShowRequiredError,
}: SurveyDraftNavigationParams) {
  const handleDraftChange = useCallback(
    (value: SurveyDraftAnswer) => {
      if (!currentQuestion) return
      setAnswerMap((prev) => {
        const next = new Map(prev)
        if (value == null || (Array.isArray(value) && value.length === 0)) {
          next.delete(currentQuestion.id)
        } else {
          next.set(currentQuestion.id, value)
        }
        return next
      })
      setShowRequiredError(false)
    },
    [currentQuestion, setAnswerMap, setShowRequiredError],
  )

  const handleBack = useCallback(() => {
    const prevId = previousQuestionId(visitedQuestionIds)
    if (prevId) {
      setVisitedQuestionIds(trimVisitedForBack)
      setLocalQuestionId(prevId)
    } else if (instrumentId) {
      navigate(`/instrument/${instrumentId}/info`)
    }
    setShowRequiredError(false)
  }, [
    visitedQuestionIds,
    instrumentId,
    navigate,
    setVisitedQuestionIds,
    setLocalQuestionId,
    setShowRequiredError,
  ])

  return { handleDraftChange, handleBack }
}
